/**
 * useControlTowerOverrides (P12)
 *
 * Override-action wrapper. Each action:
 *   1. Checks active override session + rate limit (consumeOverrideAction).
 *   2. Snapshots before-state.
 *   3. Calls the canonical writer (assign / offer engine / setJobs).
 *      Invariants stay intact — overrides do not bypass eligibility,
 *      pay-profile, or assignment guards.
 *   4. Records audit + override-flag entries.
 *
 * Returns { ok: true } on success, { ok: false, reason } otherwise.
 */

import { useCallback } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useAssignContractor } from "@/hooks/useAssignContractor";
import { useMarketplaceOffers } from "@/hooks/useMarketplaceOffers";
import { consumeOverrideAction } from "@/data/controlTower/session";
import { recordControlTowerAudit, type ControlTowerAuditEventType } from "@/data/controlTower/audit";
import { recordControlTowerOverride, type ControlTowerOverrideAction } from "@/data/controlTower/overrides";

export type OverrideActionFailureReason =
  | "not_active"
  | "rate_limited"
  | "not_found"
  | "writer_failed";

export type OverrideActionResult =
  | { ok: true }
  | { ok: false; reason: OverrideActionFailureReason; detail?: string };

export function useControlTowerOverrides(adminId: string) {
  const { jobs, contractors, marketplaceOffers, payProfiles, setJobs } = useAppData();
  const assign = useAssignContractor();
  const offers = useMarketplaceOffers();

  const gate = useCallback((): OverrideActionResult | null => {
    const r = consumeOverrideAction();
    if (r.ok === true) return null;
    return { ok: false, reason: (r as { ok: false; reason: "not_active" | "rate_limited" }).reason };
  }, []);

  const log = useCallback(
    (
      eventType: ControlTowerAuditEventType,
      ids: { jobId?: string; contractorId?: string; offerId?: string },
      before: Record<string, unknown>,
      after: Record<string, unknown>,
    ) => {
      recordControlTowerAudit({
        eventType,
        adminId,
        ...ids,
        beforeState: before,
        afterState: after,
      });
    },
    [adminId],
  );

  const flag = useCallback(
    (
      action: ControlTowerOverrideAction,
      entityType: "job" | "offer" | "contractor",
      entityId: string,
    ) => {
      recordControlTowerOverride({ action, entityType, entityId, adminId });
    },
    [adminId],
  );

  // ── Assign / Reassign ──────────────────────────────────────
  const assignContractorAction = useCallback(
    (jobId: string, contractorId: string): OverrideActionResult => {
      const blocked = gate();
      if (blocked) return blocked;
      const job = jobs.find((j) => j.id === jobId);
      if (!job) return { ok: false, reason: "not_found" };
      const before = { assignedSubcontractorId: job.assignedSubcontractorId ?? null };
      const result = assign({ jobId, contractorId, source: "manual", actor: adminId });
      if (!result.changed) return { ok: false, reason: "writer_failed" };
      const after = { assignedSubcontractorId: contractorId };
      const isReassign = before.assignedSubcontractorId !== null;
      log(isReassign ? "reassign_contractor" : "assign_contractor", { jobId, contractorId }, before, after);
      flag(isReassign ? "reassign_contractor" : "assign_contractor", "job", jobId);
      flag(isReassign ? "reassign_contractor" : "assign_contractor", "contractor", contractorId);
      return { ok: true };
    },
    [gate, jobs, assign, adminId, log, flag],
  );

  // ── Withdraw offer ─────────────────────────────────────────
  const withdrawOfferAction = useCallback(
    (offerId: string): OverrideActionResult => {
      const blocked = gate();
      if (blocked) return blocked;
      const offer = marketplaceOffers.find((o) => o.id === offerId);
      if (!offer) return { ok: false, reason: "not_found" };
      const before = { status: offer.status };
      const r = offers.withdraw(offerId, adminId, "Control Tower override");
      if (r.ok !== true) return { ok: false, reason: "writer_failed", detail: String((r as { reason?: string }).reason ?? "") };
      log("withdraw_offer", { offerId, jobId: offer.jobId, contractorId: offer.contractorId }, before, { status: "withdrawn" });
      flag("withdraw_offer", "offer", offerId);
      flag("withdraw_offer", "job", offer.jobId);
      return { ok: true };
    },
    [gate, marketplaceOffers, offers, adminId, log, flag],
  );

  // ── Resend offer (creates a new offer to the same contractor) ─
  const resendOfferAction = useCallback(
    (offerId: string): OverrideActionResult => {
      const blocked = gate();
      if (blocked) return blocked;
      const prior = marketplaceOffers.find((o) => o.id === offerId);
      if (!prior) return { ok: false, reason: "not_found" };
      const job = jobs.find((j) => j.id === prior.jobId);
      const contractor = contractors.find((c) => c.id === prior.contractorId);
      if (!job || !contractor) return { ok: false, reason: "not_found" };
      const r = offers.create({ job, contractor, payProfiles, sentBy: adminId });
      if (r.ok !== true) return { ok: false, reason: "writer_failed", detail: String((r as { reason?: string }).reason ?? "") };
      log(
        "resend_offer",
        { offerId: r.offer.id, jobId: job.id, contractorId: contractor.id },
        { priorOfferId: offerId, priorStatus: prior.status },
        { newOfferId: r.offer.id, status: "pending" },
      );
      flag("resend_offer", "offer", r.offer.id);
      flag("resend_offer", "job", job.id);
      return { ok: true };
    },
    [gate, marketplaceOffers, jobs, contractors, payProfiles, offers, adminId, log, flag],
  );

  // ── Set / clear schedule date ──────────────────────────────
  const setScheduleDateAction = useCallback(
    (jobId: string, isoDate: string | null): OverrideActionResult => {
      const blocked = gate();
      if (blocked) return blocked;
      const job = jobs.find((j) => j.id === jobId);
      if (!job) return { ok: false, reason: "not_found" };
      const before = { scheduledInstallDate: job.scheduledInstallDate };
      setJobs((prev) =>
        prev.map((j) =>
          j.id === jobId
            ? { ...j, scheduledInstallDate: isoDate ?? "" }
            : j,
        ),
      );
      const eventType = isoDate ? "set_schedule_date" : "clear_schedule_date";
      const action: ControlTowerOverrideAction = isoDate ? "set_schedule_date" : "clear_schedule_date";
      log(eventType, { jobId }, before, { scheduledInstallDate: isoDate ?? "" });
      flag(action, "job", jobId);
      return { ok: true };
    },
    [gate, jobs, setJobs, log, flag],
  );

  return {
    assignContractor: assignContractorAction,
    reassignContractor: assignContractorAction,
    withdrawOffer: withdrawOfferAction,
    resendOffer: resendOfferAction,
    setScheduleDate: setScheduleDateAction,
    clearScheduleDate: (jobId: string) => setScheduleDateAction(jobId, null),
  };
}
